import type { Pagination } from "@modules/shared/models/pagination.model";

export type PaginationMeta = Pagination;

interface PaginatedResponse<T> {
  items: T[];
  hasMore: boolean;
}

type AnyRecord = Record<string, unknown>;

const asRecord = (value: unknown): AnyRecord | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as AnyRecord)
    : null;

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Read paging meta from standard `{ pagination }` or legacy nested shapes. */
export const extractPagination = (response: unknown): PaginationMeta | null => {
  if (!response) return null;
  const root = asRecord(response);
  if (!root) return null;

  const fromBlock = (block: AnyRecord): PaginationMeta => ({
    total: num(block.total ?? block.totalItems ?? block.total_items),
    page: num(block.page) || 1,
    limit: num(block.limit) || 10,
  });

  const top = asRecord(root.pagination);
  if (top && ("total" in top || "page" in top || "limit" in top)) {
    return fromBlock(top);
  }

  const meta = asRecord(root.meta);
  if (meta && ("total" in meta || "page" in meta || "limit" in meta)) {
    return fromBlock(meta);
  }

  const data = asRecord(root.data);
  if (data) {
    const nested = asRecord(data.pagination);
    if (nested) return fromBlock(nested);
    if ("total" in data || "page" in data || "limit" in data) {
      return fromBlock(data);
    }
  }

  if (
    ("total" in root || "page" in root || "limit" in root) &&
    (Array.isArray(root.items) || Array.isArray(root.data))
  ) {
    return fromBlock(root);
  }

  return null;
};

export const extractList = <T>(response: unknown): T[] => {
  if (!response) return [];
  if (Array.isArray(response)) return response as T[];

  const root = asRecord(response);
  if (!root) return [];

  if (Array.isArray(root.data)) return root.data as T[];
  if (Array.isArray(root.items)) return root.items as T[];
  if (Array.isArray(root.docs)) return root.docs as T[];

  const data = asRecord(root.data);
  if (data) {
    if (Array.isArray(data.data)) return data.data as T[];
    if (Array.isArray(data.docs)) return data.docs as T[];
    if (Array.isArray(data.items)) return data.items as T[];
  }

  if (Array.isArray(root.result)) return root.result as T[];

  console.warn("[extractList] unknown response shape", response);
  return [];
};

/** Standard list + paging parse (matches clients/trainers services). */
export const parsePaginatedResponse = <T>(
  response: unknown,
): { items: T[]; pagination: PaginationMeta | null } => ({
  items: extractList<T>(response),
  pagination: extractPagination(response),
});

/** Unwrap a single entity from `{ data: entity }` API envelopes. */
export const unwrapEntity = <T>(response: unknown): T => {
  if (!response) return response as T;
  const root = asRecord(response);
  if (!root || !("data" in root)) return response as T;

  const data = root.data;
  if (data === undefined || data === null) return response as T;
  if (Array.isArray(data)) return response as T;

  const nested = asRecord(data);
  if (nested && Array.isArray(nested.data)) return response as T;

  return data as T;
};

export async function fetchAllPaginated<T>(
  fetchPage: (page: number, pageSize: number) => Promise<PaginatedResponse<T>>,
  pageSize = 100,
  maxPages = 100,
): Promise<T[]> {
  let allItems: T[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore && page <= maxPages) {
    const { items, hasMore: more } = await fetchPage(page, pageSize);
    if (items.length === 0) break;
    allItems = allItems.concat(items);
    hasMore = more;
    page++;
  }
  return allItems;
}

export const buildPagingQuery = (
  page: number,
  limit: number,
  sort?: 1 | -1,
): string => {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (typeof sort === "number") params.set("sort", String(sort));
  return params.toString();
};

export const shouldContinuePaging = (
  items: unknown[],
  page: number,
  pageSize: number,
  response: unknown,
): boolean => {
  if (items.length === 0) return false;

  const pagination = extractPagination(response);
  if (!pagination) return items.length === pageSize;

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  if (totalPages > 0) return page < totalPages;
  if (pagination.total > 0) return page * pageSize < pagination.total;
  return items.length === pageSize;
};
